import { QueryOperation } from "../code/search-query/search-query";
import { Pagination, PaginationInput } from "../repository/data.default";
import { retry } from "../utils/http";

/** Optional item→option tweaks shared by the search-request factories. */
export interface SearchRequestOptions<T> {
  /** Option value field — defaults to `uid` (e.g. join rows keyed by `userUid`). */
  readonly valueField?: keyof T & string;
  /** Secondary option line (e.g. the company name behind a user). */
  readonly subDisplayField?: keyof T & string;
}

// The shared item→option mapping every factory emits.
function createOptionMapper<T extends { uid: string }>(
  nameField: keyof T & string,
  displayFn?: (item: T) => string,
  options?: SearchRequestOptions<T>,
) {
  return (dataItem: T) => ({
    display: displayFn ? displayFn(dataItem) : String(dataItem[nameField]),
    subDisplay: options?.subDisplayField
      ? (dataItem[options.subDisplayField] as string | undefined)
      : undefined,
    value: options?.valueField
      ? (dataItem[options.valueField] as unknown as string)
      : dataItem.uid,
    dataItem,
  });
}

export function createSearchRequest<T extends { uid: string }>(
  getFn: (input: PaginationInput) => Promise<Pagination<T>>,
  nameField: keyof T & string,
  displayFn?: (item: T) => string,
  options?: SearchRequestOptions<T>,
) {
  const toOption = createOptionMapper(nameField, displayFn, options);
  return (search: string) => {
    return retry(() =>
      getFn({
        searchQuery: {
          conditions: [
            {
              field: nameField,
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) => data.map(toOption));
  };
}

/**
 * Like {@link createSearchRequest}, but scoped to a parent entity (e.g. departments of the
 * selected company). `getScope` is evaluated at request time (read it off the form), so the
 * dropdown only ever offers children of the current parent. Returns an empty list while no
 * parent is selected. Pair with a `dependsOn` on the child control so a stale child selection
 * is cleared when the parent changes.
 */
export function createScopedSearchRequest<T extends { uid: string }>(
  getFn: (input: PaginationInput) => Promise<Pagination<T>>,
  nameField: keyof T & string,
  scopeField: keyof T & string,
  getScope: () => string | undefined | null,
  displayFn?: (item: T) => string,
  options?: SearchRequestOptions<T>,
) {
  const toOption = createOptionMapper(nameField, displayFn, options);
  return (search: string) => {
    const scope = getScope();
    if (!scope) {
      return Promise.resolve([]);
    }

    return retry(() =>
      getFn({
        searchQuery: {
          conditions: [
            {
              field: nameField,
              operation: QueryOperation.StartsWith,
              values: [search],
            },
            {
              field: scopeField,
              operation: QueryOperation.Equal,
              values: [scope],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) => data.map(toOption));
  };
}

/**
 * Soft-scoped variant for **filter** comboboxes: searches the full set by default, but **narrows** by any
 * scope whose value is currently set (read live from the form via `getScopes`). Unlike
 * {@link createScopedSearchRequest} (which returns nothing until a parent is chosen), this keeps the
 * dropdown usable platform-wide while a chosen company/department progressively scopes it. Pair each child
 * with a `dependsOn` so a stale selection clears when its parent changes.
 */
/**
 * A combobox "request" backed by a FIXED in-memory option set (no endpoint) — for enum/boolean filters such
 * as Type, Published/Draft, Public/Private, Required/Optional, completion Status. Filters by case-insensitive
 * prefix on the display text, mirroring the async-request contract the DynamicForm combobox expects. Use this
 * instead of hand-rolling `(search) => Promise.resolve(options.filter(...))` per view.
 */
export function createStaticOptionsRequest<T = string>(
  options: readonly { display: string; value: T }[],
) {
  return (search: string) =>
    Promise.resolve(
      options.filter((o) =>
        o.display.toLowerCase().startsWith((search ?? "").toLowerCase()),
      ),
    );
}

export function createScopedFilterRequest<T extends { uid: string }>(
  getFn: (input: PaginationInput) => Promise<Pagination<T>>,
  nameField: keyof T & string,
  getScopes: () => readonly (readonly [
    field: string,
    value: string | undefined | null,
  ])[],
  displayFn?: (item: T) => string,
  options?: SearchRequestOptions<T>,
) {
  const toOption = createOptionMapper(nameField, displayFn, options);
  return (search: string) => {
    const conditions: {
      field: string;
      operation: QueryOperation;
      values: string[];
    }[] = [
      { field: nameField, operation: QueryOperation.StartsWith, values: [search] },
    ];

    for (const [field, value] of getScopes()) {
      if (value) {
        conditions.push({ field, operation: QueryOperation.Equal, values: [value] });
      }
    }

    return retry(() =>
      getFn({
        searchQuery: { conditions },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) => data.map(toOption));
  };
}
